import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Cake, Users, UserCircle, PartyPopper, Calendar, Filter, Sparkles, Plus, Download, Key, Clock, FileText, ClipboardList } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { format, parseISO } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BirthdayDashboard() {
  const [weeklyData, setWeeklyData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [tokenStatus, setTokenStatus] = useState(null);
  const [autoSyncStatus, setAutoSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportType, setReportType] = useState("camper");
  const [reportStartMonth, setReportStartMonth] = useState("1");
  const [reportEndMonth, setReportEndMonth] = useState("12");
  const [reportFormat, setReportFormat] = useState("csv");
  const [gallyFormat, setGallyFormat] = useState("xlsx");
  const [gallyStartMonth, setGallyStartMonth] = useState("1");
  const [gallyEndMonth, setGallyEndMonth] = useState("12");
  const [newPerson, setNewPerson] = useState({
    name: "",
    date_of_birth: "",
    group_name: "",
    person_type: "camper"
  });

  const fetchTokenStatus = async () => {
    try {
      const response = await axios.get(`${API}/token/status`);
      setTokenStatus(response.data);
    } catch (error) {
      console.error("Error fetching token status:", error);
    }
  };

  const fetchAutoSyncStatus = async () => {
    try {
      const response = await axios.get(`${API}/autosync/status`);
      setAutoSyncStatus(response.data);
    } catch (error) {
      console.error("Error fetching auto-sync status:", error);
    }
  };

  const fetchWeeklyBirthdays = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedGroup !== "all") params.append("group", selectedGroup);
      if (selectedType !== "all") params.append("person_type", selectedType);
      
      const response = await axios.get(`${API}/birthdays/weekly?${params.toString()}`);
      setWeeklyData(response.data);
    } catch (error) {
      console.error("Error fetching birthdays:", error);
      toast.error("Failed to fetch birthday data");
    }
  }, [selectedGroup, selectedType]);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/groups`);
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error("Error fetching groups:", error);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await axios.get(`${API}/sync/status`);
      setSyncStatus(response.data);
    } catch (error) {
      console.error("Error fetching sync status:", error);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/sync`);
      if (response.data.success) {
        toast.success(response.data.message);
        // Poll for sync completion
        const pollSync = setInterval(async () => {
          try {
            const progress = await axios.get(`${API}/sync/progress`);
            if (!progress.data.in_progress) {
              clearInterval(pollSync);
              setSyncing(false);
              toast.success("Sync complete! Refreshing data...");
              await Promise.all([fetchWeeklyBirthdays(), fetchGroups(), fetchSyncStatus(), fetchTokenStatus()]);
            }
          } catch (e) {
            // keep polling
          }
        }, 10000);
        // Stop polling after 5 minutes max
        setTimeout(() => { clearInterval(pollSync); setSyncing(false); }, 300000);
      } else {
        toast.error(response.data.message || "Sync returned no data");
      }
    } catch (error) {
      console.error("Sync error:", error);
      toast.error("Sync failed. Check API connection or try refreshing token.");
      setSyncing(false);
    }
  };

  const handleAddPerson = async (personData) => {
    try {
      await axios.post(`${API}/persons`, personData);
      toast.success(`Added ${personData.name}!`);
      await Promise.all([fetchWeeklyBirthdays(), fetchGroups(), fetchSyncStatus()]);
      return true;
    } catch (error) {
      console.error("Add person error:", error);
      toast.error("Failed to add person");
      return false;
    }
  };

  const handleSubmitNewPerson = async (e) => {
    e.preventDefault();
    if (!newPerson.name || !newPerson.date_of_birth) {
      toast.error("Please fill in name and date of birth");
      return;
    }
    const success = await handleAddPerson(newPerson);
    if (success) {
      setNewPerson({ name: "", date_of_birth: "", group_name: "", person_type: "camper" });
      setShowAddDialog(false);
    }
  };

  const handleDownloadReport = () => {
    window.open(`${API}/birthdays/download`, '_blank');
    toast.success("Downloading birthday report...");
  };

  const MONTH_OPTIONS = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const handleDownloadDobReport = () => {
    const params = new URLSearchParams({
      person_type: reportType,
      start_month: reportStartMonth,
      end_month: reportEndMonth,
      file_format: reportFormat,
    });
    window.open(`${API}/birthdays/report/download?${params.toString()}`, '_blank');
    const typeLabel = reportType === "staff" ? "Staff" : "Camper";
    const startLabel = MONTH_OPTIONS.find(m => m.value === reportStartMonth)?.label;
    const endLabel = MONTH_OPTIONS.find(m => m.value === reportEndMonth)?.label;
    toast.success(`Downloading ${typeLabel} DOB report (${startLabel} - ${endLabel})...`);
  };

  const handleDownloadGallyReport = () => {
    const params = new URLSearchParams({
      file_format: gallyFormat,
      start_month: gallyStartMonth,
      end_month: gallyEndMonth,
    });
    window.open(`${API}/reports/gally?${params.toString()}`, '_blank');
    const startLabel = MONTH_OPTIONS.find(m => m.value === gallyStartMonth)?.label;
    const endLabel = MONTH_OPTIONS.find(m => m.value === gallyEndMonth)?.label;
    toast.success(`Downloading Gally Report (${startLabel} - ${endLabel})...`);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchWeeklyBirthdays(), fetchGroups(), fetchSyncStatus(), fetchTokenStatus(), fetchAutoSyncStatus()]);
      setLoading(false);
    };
    loadData();
    
    // Check token status every 5 minutes
    const tokenInterval = setInterval(fetchTokenStatus, 5 * 60 * 1000);
    return () => clearInterval(tokenInterval);
  }, [fetchWeeklyBirthdays]);

  const formatDateDisplay = (dateStr) => {
    try {
      return format(parseISO(dateStr), "MMM d");
    } catch {
      return dateStr;
    }
  };

  const getTotalBirthdaysThisWeek = () => {
    if (!weeklyData?.days) return 0;
    return weeklyData.days.reduce((sum, day) => sum + day.birthdays.length, 0);
  };

  const getTodaysBirthdays = () => {
    if (!weeklyData?.days) return [];
    const today = weeklyData.days.find(d => d.is_today);
    return today?.birthdays || [];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="font-['Outfit'] text-slate-500">Loading birthday data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="header sticky top-0 z-50 px-4 md:px-8 py-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img 
              src="/rr-logo.png" 
              alt="Rolling River Day Camp" 
              className="w-14 h-14 rounded-xl object-contain bg-white shadow-lg"
            />
            <div>
              <h1 className="font-['Fredoka'] text-2xl font-bold text-slate-800" data-testid="app-title">
                RRDC Confetti OS
              </h1>
              <p className="font-['Outfit'] text-sm text-slate-500">Birthday Tracker</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap justify-center">
            {/* Filters */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                <SelectTrigger className="w-[160px] rounded-full border-2" data-testid="group-filter">
                  <SelectValue placeholder="All Groups" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Groups</SelectItem>
                  {groups.map((group) => (
                    <SelectItem key={group} value={group}>
                      {group}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="w-[140px] rounded-full border-2" data-testid="type-filter">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="camper">Campers</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Add Person Button */}
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full border-2 border-dashed border-pink-300 hover:border-pink-400 hover:bg-pink-50"
                  data-testid="add-person-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Person
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-['Fredoka'] text-xl flex items-center gap-2">
                    <Cake className="w-5 h-5 text-pink-500" />
                    Add New Person
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitNewPerson} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-['Outfit']">Name *</Label>
                    <Input
                      id="name"
                      placeholder="Enter full name"
                      value={newPerson.name}
                      onChange={(e) => setNewPerson({ ...newPerson, name: e.target.value })}
                      className="rounded-xl"
                      data-testid="add-person-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dob" className="font-['Outfit']">Date of Birth *</Label>
                    <Input
                      id="dob"
                      type="date"
                      value={newPerson.date_of_birth}
                      onChange={(e) => setNewPerson({ ...newPerson, date_of_birth: e.target.value })}
                      className="rounded-xl"
                      data-testid="add-person-dob"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="group" className="font-['Outfit']">Group/Cabin</Label>
                    <Input
                      id="group"
                      placeholder="e.g., Cabin Pine, Staff"
                      value={newPerson.group_name}
                      onChange={(e) => setNewPerson({ ...newPerson, group_name: e.target.value })}
                      className="rounded-xl"
                      data-testid="add-person-group"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-['Outfit']">Type</Label>
                    <Select
                      value={newPerson.person_type}
                      onValueChange={(value) => setNewPerson({ ...newPerson, person_type: value })}
                    >
                      <SelectTrigger className="rounded-xl" data-testid="add-person-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="camper">Camper</SelectItem>
                        <SelectItem value="staff">Staff</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 rounded-full"
                      onClick={() => setShowAddDialog(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 rounded-full bg-gradient-to-r from-pink-500 to-orange-400 text-white"
                      data-testid="add-person-submit"
                    >
                      Add Person
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            
            {/* Download Report Button */}
            <Button
              onClick={handleDownloadReport}
              variant="outline"
              className="rounded-full border-2 border-green-300 hover:border-green-400 hover:bg-green-50"
              data-testid="download-report-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Report
            </Button>

            {/* DOB Reports Button */}
            <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full border-2 border-indigo-300 hover:border-indigo-400 hover:bg-indigo-50"
                  data-testid="dob-reports-button"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  DOB Reports
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-['Fredoka'] text-xl flex items-center gap-2">
                    <FileText className="w-5 h-5 text-indigo-500" />
                    DOB Report
                  </DialogTitle>
                  <DialogDescription className="font-['Outfit'] text-sm text-slate-500">
                    Select type, month range, and format to download.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-5 mt-4">
                  <div className="space-y-2">
                    <Label className="font-['Outfit'] font-medium">Type</Label>
                    <Select value={reportType} onValueChange={setReportType}>
                      <SelectTrigger className="rounded-xl" data-testid="report-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="camper">Campers</SelectItem>
                        <SelectItem value="staff">Staff</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="font-['Outfit'] font-medium">From</Label>
                      <Select value={reportStartMonth} onValueChange={setReportStartMonth}>
                        <SelectTrigger className="rounded-xl" data-testid="report-start-month">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MONTH_OPTIONS.map((m) => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-['Outfit'] font-medium">To</Label>
                      <Select value={reportEndMonth} onValueChange={setReportEndMonth}>
                        <SelectTrigger className="rounded-xl" data-testid="report-end-month">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MONTH_OPTIONS.map((m) => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-['Outfit'] font-medium">Format</Label>
                    <Select value={reportFormat} onValueChange={setReportFormat}>
                      <SelectTrigger className="rounded-xl" data-testid="report-format-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="csv">CSV (Spreadsheet)</SelectItem>
                        <SelectItem value="pdf">PDF</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 rounded-full"
                      onClick={() => setShowReportDialog(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 rounded-full bg-gradient-to-r from-indigo-500 to-blue-500 text-white hover:from-indigo-600 hover:to-blue-600"
                      onClick={handleDownloadDobReport}
                      data-testid="report-download-button"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Gally Report Button */}
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full border-2 border-cyan-300 hover:border-cyan-400 hover:bg-cyan-50"
                  data-testid="gally-report-button"
                >
                  <ClipboardList className="w-4 h-4 mr-2" />
                  Gally Report
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-['Fredoka'] text-xl flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-cyan-500" />
                    Gally Report
                  </DialogTitle>
                  <DialogDescription className="font-['Outfit'] text-sm text-slate-500">
                    Campers (no CIT) with names, groups, and birthdays.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-5 mt-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="font-['Outfit'] font-medium">From</Label>
                      <Select value={gallyStartMonth} onValueChange={setGallyStartMonth}>
                        <SelectTrigger className="rounded-xl" data-testid="gally-start-month">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MONTH_OPTIONS.map((m) => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-['Outfit'] font-medium">To</Label>
                      <Select value={gallyEndMonth} onValueChange={setGallyEndMonth}>
                        <SelectTrigger className="rounded-xl" data-testid="gally-end-month">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MONTH_OPTIONS.map((m) => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-['Outfit'] font-medium">Format</Label>
                    <Select value={gallyFormat} onValueChange={setGallyFormat}>
                      <SelectTrigger className="rounded-xl" data-testid="gally-format-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="xlsx">Excel (Tab per Group)</SelectItem>
                        <SelectItem value="csv">CSV</SelectItem>
                        <SelectItem value="pdf">PDF</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      className="flex-1 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-600 hover:to-blue-600"
                      onClick={handleDownloadGallyReport}
                      data-testid="gally-download-button"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Sync Button */}
            <Button
              onClick={handleSync}
              disabled={syncing}
              className="sync-button"
              data-testid="sync-button"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync'}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="stats-card" data-testid="stats-total">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Users className="w-5 h-5 text-pink-500" />
            </div>
            <p className="font-['Fredoka'] text-3xl font-bold text-slate-800">
              {syncStatus?.total_count || 0}
            </p>
            <p className="font-['Outfit'] text-sm text-slate-500">Total People</p>
          </Card>
          
          <Card className="stats-card" data-testid="stats-campers">
            <div className="flex items-center justify-center gap-2 mb-2">
              <UserCircle className="w-5 h-5 text-blue-500" />
            </div>
            <p className="font-['Fredoka'] text-3xl font-bold text-slate-800">
              {syncStatus?.camper_count || 0}
            </p>
            <p className="font-['Outfit'] text-sm text-slate-500">Campers</p>
          </Card>
          
          <Card className="stats-card" data-testid="stats-staff">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
            </div>
            <p className="font-['Fredoka'] text-3xl font-bold text-slate-800">
              {syncStatus?.staff_count || 0}
            </p>
            <p className="font-['Outfit'] text-sm text-slate-500">Staff</p>
          </Card>
          
          <Card className="stats-card" data-testid="stats-week">
            <div className="flex items-center justify-center gap-2 mb-2">
              <PartyPopper className="w-5 h-5 text-orange-500" />
            </div>
            <p className="font-['Fredoka'] text-3xl font-bold text-slate-800">
              {getTotalBirthdaysThisWeek()}
            </p>
            <p className="font-['Outfit'] text-sm text-slate-500">This Week</p>
          </Card>
        </div>

        {/* Today's Birthdays Highlight */}
        {getTodaysBirthdays().length > 0 && (
          <Card className="mb-8 border-2 border-pink-300 bg-gradient-to-br from-pink-50 to-orange-50 today-card" data-testid="today-birthdays">
            <CardHeader>
              <CardTitle className="font-['Fredoka'] text-xl flex items-center gap-2 text-pink-600">
                <PartyPopper className="w-6 h-6" />
                Today's Birthdays!
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {getTodaysBirthdays().map((person, idx) => (
                  <div
                    key={idx}
                    className={`person-badge ${person.person_type} animate-bounce-in`}
                    style={{ animationDelay: `${idx * 100}ms` }}
                    data-testid={`today-birthday-${idx}`}
                  >
                    <Cake className="w-4 h-4" />
                    <span className="font-semibold">{person.name}</span>
                    <span className="opacity-70">• {person.person_type === 'staff' ? 'Staff' : 'Camper'}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Week Period */}
        {weeklyData && (
          <div className="flex items-center gap-2 mb-6">
            <Calendar className="w-5 h-5 text-slate-400" />
            <span className="font-['Outfit'] text-slate-600">
              Week of {formatDateDisplay(weeklyData.week_start)} - {formatDateDisplay(weeklyData.week_end)}
            </span>
          </div>
        )}

        {/* Weekly Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="weekly-grid">
          {weeklyData?.days?.map((day, index) => (
            <Card
              key={day.day_name}
              className={`day-card ${day.is_today ? 'is-today' : ''}`}
              data-testid={`day-card-${day.day_name.toLowerCase()}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-['Fredoka'] text-lg">
                    {day.day_name}
                  </CardTitle>
                  {day.is_today && (
                    <Badge className="bg-gradient-to-r from-pink-500 to-orange-400 text-white border-0">
                      Today
                    </Badge>
                  )}
                </div>
                <p className="font-['Outfit'] text-sm text-slate-500">
                  {formatDateDisplay(day.date)}
                </p>
              </CardHeader>
              <CardContent>
                {day.birthdays.length > 0 ? (
                  <div className="space-y-2">
                    {day.birthdays.map((person, idx) => (
                      <div
                        key={idx}
                        className={`person-badge ${person.person_type} w-full justify-start`}
                        data-testid={`birthday-${day.day_name.toLowerCase()}-${idx}`}
                      >
                        <Cake className="w-4 h-4 flex-shrink-0" />
                        <span className="font-semibold truncate">{person.name}</span>
                        <Badge 
                          variant="outline" 
                          className={`ml-auto flex-shrink-0 text-xs ${
                            person.person_type === 'staff' 
                              ? 'border-purple-300 text-purple-600' 
                              : 'border-blue-300 text-blue-600'
                          }`}
                        >
                          {person.person_type === 'staff' ? 'Staff' : 'Camper'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state py-6">
                    <Cake className="w-8 h-8 text-slate-300 mb-2" />
                    <p className="text-sm text-slate-400">No birthdays</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Footer Info */}
        <div className="mt-8 text-center space-y-2">
          {syncStatus?.last_sync && (
            <p className="font-['Outfit'] text-sm text-slate-400">
              Last synced: {format(new Date(syncStatus.last_sync), "MMM d, yyyy 'at' h:mm a")}
            </p>
          )}
          {tokenStatus && (
            <div className="flex items-center justify-center gap-2">
              <Key className={`w-3 h-3 ${tokenStatus.status === 'valid' ? 'text-green-500' : 'text-red-500'}`} />
              <span className={`font-['Outfit'] text-xs ${tokenStatus.status === 'valid' ? 'text-green-600' : 'text-red-600'}`}>
                API Token: {tokenStatus.status === 'valid' ? `Valid (${tokenStatus.expires_in_minutes}m)` : 'Expired'}
              </span>
              {tokenStatus.can_auto_refresh && (
                <span className="font-['Outfit'] text-xs text-slate-400">• Auto-refresh enabled</span>
              )}
            </div>
          )}
          {autoSyncStatus && autoSyncStatus.enabled && (
            <div className="flex items-center justify-center gap-2 mt-1">
              <Clock className="w-3 h-3 text-blue-500" />
              <span className="font-['Outfit'] text-xs text-blue-600">
                Auto-sync: {autoSyncStatus.schedule_description}
              </span>
              {autoSyncStatus.next_run && (
                <span className="font-['Outfit'] text-xs text-slate-400">
                  • Next: {format(new Date(autoSyncStatus.next_run), "EEE, MMM d 'at' h:mm a")}
                </span>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
